/* eslint-disable import/no-default-export */
import API from 'app/gamma/src/api';
import { closePopovers } from 'app/gamma/src/modules/state/ui/popovers';
import { getMe } from 'app/gamma/src/selectors/members';
import { MemberResponse, StandardThunkAction } from 'app/gamma/src/types';
import { Action, actionCreator } from 'app/gamma/src/util/actions';
import { createReducer } from 'app/gamma/src/util/reducers';

import { setLocalStorageUser } from 'app/gamma/src/util/application-storage';

const LOGIN_GOOGLE = Symbol('models/LOGIN_GOOGLE');
const LOG_OUT = Symbol('models/LOG_OUT');
const LOGOUT_OTHER_SESSIONS = Symbol('models/LOGOUT_OTHER_SESSIONS');
const SET_MEMBER = Symbol('models/SET_MEMBER');
const USE_TOKEN = Symbol('models/USE_TOKEN');

export const UPDATE_PASSWORD_REQUEST = Symbol('models/UPDATE_PASSWORD_REQUEST');
export const UPDATE_PASSWORD_SUCCESS = Symbol('models/UPDATE_PASSWORD_SUCCESS');
export const UPDATE_PASSWORD_ERROR = Symbol('models/UPDATE_PASSWORD_ERROR');

type LoginAction = Action<typeof LOGIN_GOOGLE, TokenPayload>;
type LogoutAction = Action<typeof LOG_OUT, LogoutModel>;
type LogoutSessionsAction = Action<typeof LOGOUT_OTHER_SESSIONS, null>;
type SetMemberAction = Action<typeof SET_MEMBER, UserIdPayload>;
type UseTokenAction = Action<typeof USE_TOKEN, TokenPayload>;

export type UpdatePasswordRequestAction = Action<
  typeof UPDATE_PASSWORD_REQUEST,
  null
>;
export type UpdatePasswordSuccessAction = Action<
  typeof UPDATE_PASSWORD_SUCCESS,
  MemberResponse
>;
export type UpdatePasswordErrorAction = Action<
  typeof UPDATE_PASSWORD_ERROR,
  string
>;

interface LogoutModel {
  returnUrl?: string;
}

interface UserIdPayload {
  idMe: string | null;
}

interface TokenPayload {
  id: string;
  token: string;
}

export interface SessionState {
  idMe: string;
  isLoggingOut: boolean;
  returnUrl?: string;
  token?: string;
}
// Reducer
const initialState: SessionState = {
  idMe: '',
  isLoggingOut: false,
};

export default createReducer(initialState, {
  [LOGIN_GOOGLE](state, { payload }: LoginAction) {
    const { id } = payload;

    return { ...state, idMe: id };
  },

  [LOG_OUT](state, { payload }: LogoutAction) {
    return { ...state, ...payload, isLoggingOut: true };
  },

  [LOGOUT_OTHER_SESSIONS](state, { payload }: LogoutSessionsAction) {
    return state;
  },

  [USE_TOKEN](state, { payload }: UseTokenAction) {
    const { id } = payload;

    return { ...state, idMe: id };
  },

  [SET_MEMBER](state, { payload }: SetMemberAction) {
    const { idMe } = payload;
    if (!idMe) {
      return state;
    }

    setLocalStorageUser(idMe);

    return { ...state, idMe };
  },
});

// Action creators
const updatePasswordRequest = actionCreator<UpdatePasswordRequestAction>(
  UPDATE_PASSWORD_REQUEST,
);
const updatePasswordSuccess = actionCreator<UpdatePasswordSuccessAction>(
  UPDATE_PASSWORD_SUCCESS,
);
export const updatePasswordError = actionCreator<UpdatePasswordErrorAction>(
  UPDATE_PASSWORD_ERROR,
);
export const loginGoogle = actionCreator<LoginAction>(LOGIN_GOOGLE);
export const logout = actionCreator<LogoutAction>(LOG_OUT);
export const logoutOtherSessions = actionCreator<LogoutSessionsAction>(
  LOGOUT_OTHER_SESSIONS,
);
export const setMember = actionCreator<SetMemberAction>(SET_MEMBER);
export const useToken = actionCreator<UseTokenAction>(USE_TOKEN);

const serverToi18n: { [key: string]: string } = {
  'invalid value for newPassword': 'not long enough',
  'The old password was incorrect': 'old password',
};

export const updatePassword = (data: {
  oldPassword?: string;
  password1: string;
  password2: string;
}): StandardThunkAction => {
  return async (dispatch, getState) => {
    dispatch(updatePasswordRequest());

    const id = getMe(getState())!.id;
    try {
      const member = await API.client.rest.post<MemberResponse>(
        `members/${id}/updatePassword`,
        {
          query: {
            oldPassword: data.oldPassword,
            newPassword: data.password1,
          },
        },
      );

      dispatch(updatePasswordSuccess(member));
      dispatch(closePopovers());
    } catch (err) {
      if (err instanceof Error) {
        const message = serverToi18n[err.message.split(':')[1].trim()];

        dispatch(updatePasswordError(message || 'unknown'));
      } else {
        dispatch(updatePasswordError(err));
      }
    }
  };
};
